import { SandboxMessage, SandboxSession } from '../model_gen/sandbox'
import GeneralService from './GeneralService'
import {Component} from 'react'
import { Observable } from 'rxjs';


export class SandboxService extends Component {
  public static getApplications(loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static getApplication(applicationId: number, loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application/${applicationId}`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static newSession(session: SandboxSession, loadingHandler: any, apiKey: string) {
    return GeneralService.post(`/sandbox/session`, JSON.stringify(session), loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }
  
  public static newSessionSync(session: SandboxSession, loadingHandler: any, apiKey: string) {
    return GeneralService.post(`/sandbox/session/sync`, JSON.stringify(session), loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static newSessionStream(session: SandboxSession, apiKey: string): Observable<any> {
    return new Observable(observer => {
      const fetchData = async () => {
        try {
          // Construct the fetch request with proper HTTP/2 headers
          const baseUrl = GeneralService.PROD_ENDPOINT;
          const url = `${baseUrl}/sandbox/session/sync/stream`;
          const options = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": apiKey,
              // Explicitly set headers for HTTP/2 streams
              "Connection": "keep-alive",
              "Accept": "application/x-ndjson"
            },
            body: JSON.stringify(session)
          };
          
          // Make the fetch request with explicit timeout handling
          const abortController = new AbortController();
          const timeoutId = setTimeout(() => abortController.abort(), 60000); // 60 second timeout
          
          const response = await fetch(url, {
            ...options,
            signal: abortController.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          // Get the reader from the response body
          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error("Response body is not readable");
          }
          
          // Read the stream with better error handling
          const decoder = new TextDecoder();
          let buffer = ""; // Buffer incomplete chunks
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // Process any remaining data in buffer
              if (buffer.trim()) {
                try {
                  observer.next(JSON.parse(buffer));
                } catch (e) {
                  console.error("Error parsing final JSON from buffer:", e);
                }
              }
              break;
            }
            
            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            
            // Process complete lines from buffer
            const lines = buffer.split("\n");
            
            // Keep the last line in the buffer if it's not complete
            buffer = lines.pop() || "";
            
            // Process each complete line
            for (const line of lines) {
              if (line.trim() === "") continue;
              
              try {
                const data = JSON.parse(line);
                observer.next(data);
              } catch (e) {
                console.error("Error parsing JSON from stream:", line, e);
              }
            }
          }
          
          // Complete the observable
          observer.complete();
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.error("Stream request timed out after 60 seconds");
          } else {
            console.error("Error in stream fetch:", error);
          }
          observer.error(error);
        }
      };
      
      // Start the fetch
      fetchData();
      
      // Return unsubscribe function
      return () => {
        console.log("Stream subscription closed");
      };
    });
  }

  public static getSessions(applicationId: number, loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application/${applicationId}/session`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static getSession(applicationId: number, sessionId: number, loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application/${applicationId}/session/${sessionId}`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static newMessage(message: SandboxMessage, loadingHandler: any, apiKey: string) {
    return GeneralService.post(`/sandbox/message`, JSON.stringify(message), loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }
  
  public static newMessageSync(message: SandboxMessage, loadingHandler: any, apiKey: string) {
    return GeneralService.post(`/sandbox/message/sync`, JSON.stringify(message), loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static newMessageStream(message: SandboxMessage, messages: SandboxMessage[], apiKey: string): Observable<any> {
    return new Observable(observer => {
      // Start the loader if provided
      const fetchData = async () => {
        try {
          // Construct the fetch request with proper HTTP/2 headers
          const baseUrl = GeneralService.PROD_ENDPOINT;
          const url = `${baseUrl}/sandbox/message/sync`;
          
          // Create the request payload with the message and all previous messages
          const payload = {
            message: message,
            messages: messages
          };
                    
          // Verify the payload is valid JSON
          try {
            // Test JSON serialization to catch any circular references
            JSON.stringify(payload);
          } catch (e) {
            console.error("[SandboxService] Error in payload format:", e);
            // If there's an error with the messages array, send just the current message
            payload.messages = [];
          }
          
          const options = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-KEY": apiKey,
              // Explicitly set headers for HTTP/2 streams
              "Connection": "keep-alive",
              "Accept": "application/x-ndjson"
            },
            body: JSON.stringify(payload)
          };
          
          // Make the fetch request with explicit timeout handling
          const abortController = new AbortController();
          const timeoutId = setTimeout(() => abortController.abort(), 60000); // 60 second timeout
          
          const response = await fetch(url, {
            ...options,
            signal: abortController.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          // Get the reader from the response body
          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error("Response body is not readable");
          }
          
          // Read the stream with better error handling
          const decoder = new TextDecoder();
          let buffer = ""; // Buffer incomplete chunks
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // Process any remaining data in buffer
              if (buffer.trim()) {
                try {
                  observer.next(JSON.parse(buffer));
                } catch (e) {
                  console.error("Error parsing final JSON from buffer:", e);
                }
              }
              break;
            }
            
            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            
            // Process complete lines from buffer
            const lines = buffer.split("\n");
            
            // Keep the last line in the buffer if it's not complete
            buffer = lines.pop() || "";
            
            // Process each complete line
            for (const line of lines) {
              if (line.trim() === "") continue;
              
              try {
                const data = JSON.parse(line);
                observer.next(data);
              } catch (e) {
                console.error("Error parsing JSON from stream:", line, e);
              }
            }
          }
          
          // Complete the observable
          observer.complete();
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.error("Stream request timed out after 60 seconds");
          } else {
            console.error("Error in stream fetch:", error);
          }
          observer.error(error);
        }
      };
      
      // Start the fetch
      fetchData();
      
      // Return unsubscribe function
      return () => {
        console.log("Stream subscription closed");
      };
    });
  }

  public static getMessages(applicationId: number, sessionId: number, loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application/${applicationId}/session/${sessionId}/message`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }

  public static getMessage(applicationId: number, sessionId: number, messageId: number, loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application/${applicationId}/session/${sessionId}/message/${messageId}`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }
  
  public static getMappedApplication(applicationId: number, loadingHandler: any, apiKey: string) {
    return GeneralService.get(`/sandbox/application/${applicationId}/map`, loadingHandler, JSON, GeneralService.PROD_ENDPOINT, apiKey)
  }
}

